<template>
  <div class="section">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/admin">Admin Settings</router-link></li>
        <li class="is-active"><router-link to="/admin/lhdn-logs" aria-current="true">LHDN API Logs</router-link></li>
      </ul>
    </nav>
    <div class="container">
      <h1 class="title">LHDN API Logs</h1>
      
      <!-- Filters -->
      <div class="field is-grouped mt-3">
        <div class="control">
          <input v-model="filters.start_date" class="input is-small" type="datetime-local" placeholder="Start Date">
        </div>
        <div class="control">
          <input v-model="filters.end_date" class="input is-small" type="datetime-local" placeholder="End Date">
        </div>
        <div class="control">
          <select v-model="filters.status" class="input is-small">
            <option value="">All Status Codes</option>
            <option value="200">200 - OK</option>
            <option value="201">201 - Created</option>
            <option value="400">400 - Bad Request</option>
            <option value="401">401 - Unauthorized</option>
            <option value="403">403 - Forbidden</option>
            <option value="404">404 - Not Found</option>
            <option value="500">500 - Internal Server Error</option>
            <option value="502">502 - Bad Gateway</option>
            <option value="503">503 - Service Unavailable</option>
          </select>
        </div>
        <div class="control">
          <input v-model="filters.uuid" class="input is-small" type="text" placeholder="Invoice UUID">
        </div>
        <div class="control">
          <button class="button is-primary is-small" @click="applyFilters">Apply Filters</button>
        </div>
        <div class="control">
          <button class="button is-warning is-small" @click="resetFilters">Reset Filters</button>
        </div>
        <div class="control">
          <button class="button is-success is-small" @click="downloadCSV">Download CSV</button>
        </div>
      </div>

      <!-- Search -->
      <div class="field mt-3">
        <div class="control">
          <input v-model="searchQuery" class="input" type="text" placeholder="Search in request URL or response data..." @input="debounceSearch">
        </div>
      </div>

      <!-- Auto Refresh Toggle -->
      <div class="field mt-3">
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="autoRefresh" @change="toggleAutoRefresh">
            Auto Refresh (30s)
          </label>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="has-text-centered mt-5">
        <div class="loader"></div>
        <p>Loading LHDN API logs...</p>
      </div>

      <!-- Logs Table -->
      <div v-else class="table-container mt-5">
        <table class="table is-fullwidth is-small is-striped">
          <thead>
            <tr>
              <th class="is-size-7">Timestamp</th>
              <th class="is-size-7">Invoice UUID</th>
              <th class="is-size-7">Request URL</th>
              <th class="is-size-7">Status Code</th>
              <th class="is-size-7">Response Preview</th>
              <th class="is-size-7">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id" :class="getLogRowClass(log.response_status)">
              <td class="is-size-7">{{ formatDateTime(log.timestamp) }}</td>
              <td class="is-size-7">
                <code class="is-size-7" v-if="log.uuid">{{ log.uuid }}</code>
                <span v-else class="has-text-grey">N/A</span>
              </td>
              <td class="is-size-7">
                <span :title="log.request_url" class="url-text">
                  {{ truncateUrl(log.request_url) }}
                </span>
              </td>
              <td class="is-size-7">
                <span class="tag" :class="getStatusTagClass(log.response_status)">
                  {{ log.response_status || 'N/A' }}
                </span>
              </td>
              <td class="is-size-7">
                <span :title="getResponsePreview(log.response_data)" class="response-preview">
                  {{ truncateResponse(getResponsePreview(log.response_data)) }}
                </span>
              </td>
              <td class="is-size-7">
                <div class="buttons are-small">
                  <button class="button is-small is-info" @click="viewFullLog(log)" :title="'View Details'">
                    <font-awesome-icon icon="eye" />
                  </button>
                  <button 
                    class="button is-small is-link" 
                    @click="copyUUID(log.uuid)" 
                    v-if="log.uuid"
                    :title="'Copy UUID'">
                    📋
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <nav class="pagination is-centered mt-5" role="navigation" aria-label="pagination" v-if="!loading">
        <button class="pagination-previous" @click="loadPreviousPage" :disabled="!previousPage">Previous</button>
        <button class="pagination-next" @click="loadNextPage" :disabled="!nextPage">Next</button>
        <ul class="pagination-list">
          <li>
            <span class="pagination-link is-current">Page {{ currentPage }}</span>
          </li>
        </ul>
      </nav>

      <!-- No logs message -->
      <div v-if="!loading && logs.length === 0" class="has-text-centered mt-5">
        <p class="subtitle">No LHDN API logs found matching your criteria.</p>
      </div>
    </div>

    <!-- Modal for full log view -->
    <div class="modal" :class="{ 'is-active': showLogModal }">
      <div class="modal-background" @click="closeLogModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">LHDN API Log Details</p>
          <button class="delete" aria-label="close" @click="closeLogModal"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="selectedLog">
            <div class="field">
              <label class="label">Timestamp:</label>
              <p>{{ formatDateTime(selectedLog.timestamp) }}</p>
            </div>
            <div class="field" v-if="selectedLog.uuid">
              <label class="label">Invoice UUID:</label>
              <div class="control">
                <input class="input" :value="selectedLog.uuid" readonly>
                <button class="button is-small is-outlined mt-2" @click="copyUUID(selectedLog.uuid)">
                  Copy UUID
                </button>
              </div>
            </div>
            <div class="field">
              <label class="label">Request URL:</label>
              <div class="box">
                <code>{{ selectedLog.request_url }}</code>
              </div>
            </div>
            <div class="field">
              <label class="label">Response Status:</label>
              <span class="tag" :class="getStatusTagClass(selectedLog.response_status)">
                {{ selectedLog.response_status || 'N/A' }}
              </span>
            </div>
            <div class="field" v-if="selectedLog.response_data">
              <label class="label">Response Data:</label>
              <div class="box">
                <pre class="response-json">{{ formatJSON(selectedLog.response_data) }}</pre>
              </div>
            </div>
            <div class="field" v-else>
              <label class="label">Response Data:</label>
              <p class="has-text-grey">No response data available</p>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="closeLogModal">Close</button>
          <button class="button is-primary" @click="downloadSingleLogJSON" v-if="selectedLog">
            Download JSON
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import lhdnLogsApi from '@/api/lhdnLogsApi';
import { toast } from 'bulma-toast';

export default {
  name: 'LHDNLogs',
  data() {
    return {
      logs: [],
      loading: false,
      filters: {
        start_date: '',
        end_date: '',
        status: '',
        uuid: '',
      },
      searchQuery: '',
      searchTimeout: null,
      autoRefresh: false,
      refreshInterval: null,
      previousPage: null,
      nextPage: null,
      currentPage: 1,
      showLogModal: false,
      selectedLog: null,
    };
  },
  methods: {
    async fetchLogs(url = null, append = false) {
      this.loading = true;
      try {
        const params = { ...this.filters };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await lhdnLogsApi.fetchLogs(url ? {} : params, url);

        if (append) {
          this.logs = [...this.logs, ...response.data.results];
        } else {
          this.logs = response.data.results;
        }
        
        this.previousPage = response.data.previous;
        this.nextPage = response.data.next;
        this.currentPage = url ? this.currentPage : 1;
        
      } catch (error) {
        console.error('Error fetching LHDN logs:', error);
        
        if (error.response && error.response.status === 503) {
          toast({
            message: "LHDN API Log model not found or not migrated. Please contact administrator.",
            type: "is-warning",
            duration: 7000,
            position: "top-right",
          });
        } else {
          toast({
            message: "Failed to load LHDN API logs. Please try again.",
            type: "is-danger",
            duration: 5000,
            position: "top-right",
          });
        }
      } finally {
        this.loading = false;
      }
    },
    
    async loadNextPage() {
      if (this.nextPage) {
        this.currentPage++;
        await this.fetchLogs(this.nextPage);
      }
    },
    
    async loadPreviousPage() {
      if (this.previousPage) {
        this.currentPage--;
        await this.fetchLogs(this.previousPage);
      }
    },
    
    applyFilters() {
      this.currentPage = 1;
      this.fetchLogs();
    },
    
    resetFilters() {
      this.filters = {
        start_date: '',
        end_date: '',
        status: '',
        uuid: '',
      };
      this.searchQuery = '';
      this.currentPage = 1;
      this.fetchLogs();
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchLogs();
      }, 500);
    },
    
    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.fetchLogs();
        }, 30000); // 30 seconds
        toast({
          message: "Auto-refresh enabled (30s intervals)",
          type: "is-info",
          duration: 3000,
          position: "top-right",
        });
      } else {
        clearInterval(this.refreshInterval);
        toast({
          message: "Auto-refresh disabled",
          type: "is-info",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    
    async downloadCSV() {
      try {
        const params = { ...this.filters };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await lhdnLogsApi.downloadLogsCSV(params);
        lhdnLogsApi.downloadCSVFile(response.data);
        
        toast({
          message: "CSV downloaded successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast({
          message: "Failed to download CSV. Please try again.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      }
    },
    
    downloadSingleLogJSON() {
      if (!this.selectedLog) return;
      
      const jsonData = JSON.stringify(this.selectedLog, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lhdn_log_${this.selectedLog.id}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        message: "JSON downloaded successfully.",
        type: "is-success",
        duration: 3000,
        position: "top-right",
      });
    },
    
    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
    
    truncateUrl(url, maxLength = 50) {
      if (!url || url.length <= maxLength) return url;
      return url.substring(0, maxLength) + '...';
    },
    
    getResponsePreview(responseData) {
      if (!responseData) return 'No response data';
      if (typeof responseData === 'string') return responseData;
      if (typeof responseData === 'object') {
        // Try to get meaningful fields from the response
        if (responseData.error) return `Error: ${responseData.error}`;
        if (responseData.message) return responseData.message;
        if (responseData.status) return `Status: ${responseData.status}`;
        return JSON.stringify(responseData);
      }
      return String(responseData);
    },
    
    truncateResponse(text, maxLength = 80) {
      if (!text || text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    formatJSON(data) {
      if (!data) return 'No data';
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2);
        } catch {
          return data;
        }
      }
      return JSON.stringify(data, null, 2);
    },
    
    getStatusTagClass(status) {
      if (!status) return 'is-light';
      
      if (status >= 200 && status < 300) return 'is-success';
      if (status >= 300 && status < 400) return 'is-info';
      if (status >= 400 && status < 500) return 'is-warning';
      if (status >= 500) return 'is-danger';
      return 'is-light';
    },
    
    getLogRowClass(status) {
      if (!status) return '';
      
      if (status >= 400 && status < 500) return 'has-background-warning-light';
      if (status >= 500) return 'has-background-danger-light';
      return '';
    },
    
    viewFullLog(log) {
      this.selectedLog = log;
      this.showLogModal = true;
    },
    
    closeLogModal() {
      this.showLogModal = false;
      this.selectedLog = null;
    },
    
    async copyUUID(uuid) {
      if (!uuid) return;
      
      try {
        await navigator.clipboard.writeText(uuid);
        toast({
          message: "UUID copied to clipboard!",
          type: "is-success",
          duration: 2000,
          position: "top-right",
        });
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = uuid;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast({
          message: "UUID copied to clipboard!",
          type: "is-success",
          duration: 2000,
          position: "top-right",
        });
      }
    },
  },
  
  async created() {
    await this.fetchLogs();
  },
  
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-container {
  overflow-x: auto;
}

.url-text, .response-preview {
  max-width: 300px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.url-text:hover, .response-preview:hover {
  color: #3273dc;
}

.field.is-grouped {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.field.is-grouped .control {
  margin-bottom: 0.5rem;
}

.response-json {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.pagination {
  margin-top: 2rem;
}

.checkbox {
  font-weight: normal;
}

.buttons {
  justify-content: flex-start;
}

.buttons .button {
  margin-right: 0.25rem;
}

code {
  background-color: #f5f5f5;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.875em;
}
</style>
